import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon, SearchOff as MissingIcon, Refresh as RetryIcon } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import config, { AGENT_TUTOR_COMING_SOON } from '../config';
import { getAgent, getActiveSession } from '../utils/agentTutorStorage';
import { tutorTokens } from '../theme/tutorTheme';
import { adminColors } from '../theme/adminTheme';
import TutorWorkspace from '../components/agentTutor/TutorWorkspace';
import { authFetch, handleApiResponse } from '../api/authFetch';
import AgentTutorComingSoon from '../components/agentTutor/AgentTutorComingSoon';
import { isStaffRole } from '../constants/roles';

// ─────────────────────────────────────────────────────────────────────────
// StudentTutorSession — a thin shell around TutorWorkspace.
//
// Its only jobs: confirm who's looking (student vs. a teacher/admin test
// driving their own agent at the same route), load the agent config, and
// hand off to the shared workspace. A teacher opening this page is test
// driving their own agent, not being tutored — it's deliberately the same
// workspace students get, so the only difference is the banner and where
// "back" goes. Nothing here is saved for a teacher's preview.
// ─────────────────────────────────────────────────────────────────────────

// A quiet skeleton of the real header + working pad, in the tutor's own
// palette — the point is that nothing here should look like a different
// product flashing by before the workspace mounts.
const SkeletonBlock = ({ w, h, ...sx }) => (
  <Box sx={{ width: w, height: h, borderRadius: 1, backgroundColor: tutorTokens.border, ...sx }} />
);

const LoadingShell = () => {
  const reduceMotion = !!useReducedMotion();
  return (
    <Box sx={{ minHeight: '100vh', width: '100%', overflowX: 'hidden', backgroundColor: tutorTokens.surface }}>
      <Box sx={{ backgroundColor: adminColors.sidebar, px: 3, py: 2 }}>
        <SkeletonBlock w={180} h={20} sx={{ backgroundColor: 'rgba(255,255,255,0.14)' }} />
      </Box>
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        <motion.div
          animate={reduceMotion ? undefined : { opacity: [0.55, 0.9, 0.55] }}
          transition={reduceMotion ? undefined : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Box
            sx={{
              borderRadius: `${tutorTokens.radiusXl}px`,
              border: `1px solid ${tutorTokens.border}`,
              backgroundColor: tutorTokens.surfaceRaised,
              p: { xs: 3, sm: 4.5 },
              textAlign: 'center',
            }}
          >
            <SkeletonBlock w="60%" h={28} sx={{ mx: 'auto', mb: 1.5 }} />
            <SkeletonBlock w="40%" h={16} sx={{ mx: 'auto', mb: 3 }} />
            <SkeletonBlock w="100%" h={48} sx={{ maxWidth: 500, mx: 'auto' }} />
          </Box>
        </motion.div>
        <Typography sx={{ ...tutorTokens.type.body, textAlign: 'center', mt: 3, color: tutorTokens.inkSoft, fontWeight: 600 }}>
          Loading your tutor…
        </Typography>
      </Box>
    </Box>
  );
};

const UnavailableState = ({ onBack }) => (
  <Box
    sx={{
      minHeight: '100vh',
      width: '100%',
      overflowX: 'hidden',
      backgroundColor: tutorTokens.surface,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
    }}
  >
    <Box
      sx={{
        maxWidth: 420,
        width: '100%',
        textAlign: 'center',
        borderRadius: `${tutorTokens.radiusXl}px`,
        border: `1px solid ${tutorTokens.border}`,
        backgroundColor: tutorTokens.surfaceRaised,
        boxShadow: tutorTokens.shadowCard,
        p: { xs: 3, sm: 4 },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          backgroundColor: tutorTokens.partialBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MissingIcon sx={{ fontSize: 28, color: tutorTokens.partial }} />
      </Box>
      <Typography sx={{ fontFamily: tutorTokens.fontUI, ...tutorTokens.type.title, color: tutorTokens.ink, mb: 1 }}>
        This tutor isn't available
      </Typography>
      <Typography sx={{ ...tutorTokens.type.bodyLg, color: tutorTokens.inkSoft, mb: 3 }}>
        The link may be out of date, the agent may still be a draft, or it may no longer be assigned to one
        of your classes. Your teacher can check its status from the Agent Tutor builder.
      </Typography>
      <Button
        variant="contained"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          backgroundColor: tutorTokens.accent,
          '&:hover': { backgroundColor: tutorTokens.accentDark },
          '&:active': { transform: 'scale(0.97)' },
        }}
      >
        Back to Agent Tutor
      </Button>
    </Box>
  </Box>
);

// Shown when the page had trouble loading and it wasn't because the student
// needs to sign in again — could be a network hiccup or a backend error, so
// we let them try again instead of sending them back to the sign-in screen.
// role="alert" so this is announced the moment it replaces the loading
// shell, same as StudentTutor.js gets for free from MUI's <Alert>.
//
// Icon circle is neutral (inkSoft on border), not tutorTokens.partial/
// partialBg (amber) — that amber is load-bearing elsewhere as the "partial
// credit / hint used" verdict color (see theme/tutorTheme.js), deliberately
// distinct from red "incorrect" so a student never confuses *got help* with
// *got it wrong*. A page-load failure isn't a verdict and shouldn't borrow
// from that palette.
const ErrorState = ({ onRetry }) => (
  <Box
    sx={{
      minHeight: '100vh',
      width: '100%',
      overflowX: 'hidden',
      backgroundColor: tutorTokens.surface,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
    }}
  >
    <Box
      role="alert"
      sx={{
        maxWidth: 420,
        width: '100%',
        textAlign: 'center',
        borderRadius: `${tutorTokens.radiusXl}px`,
        border: `1px solid ${tutorTokens.border}`,
        backgroundColor: tutorTokens.surfaceRaised,
        boxShadow: tutorTokens.shadowCard,
        p: { xs: 3, sm: 4 },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          backgroundColor: tutorTokens.border,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <RetryIcon sx={{ fontSize: 28, color: tutorTokens.inkSoft }} />
      </Box>
      <Typography sx={{ fontFamily: tutorTokens.fontUI, fontWeight: 600, fontSize: '1.3rem', color: tutorTokens.ink, mb: 1 }}>
        We couldn't load your tutor
      </Typography>
      <Typography sx={{ color: tutorTokens.inkSoft, fontSize: '0.9rem', mb: 3 }}>
        This didn't work. Try again — if it keeps happening, tell your teacher.
      </Typography>
      <Button
        variant="contained"
        startIcon={<RetryIcon />}
        onClick={onRetry}
        sx={{
          minHeight: 44,
          backgroundColor: tutorTokens.accent,
          '&:hover': { backgroundColor: tutorTokens.accentDark },
          '&:active': { transform: 'scale(0.97)' },
        }}
      >
        Try again
      </Button>
    </Box>
  </Box>
);

const StudentTutorSession = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [agent, setAgent] = useState(null);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [initialSession, setInitialSession] = useState(null);

  useEffect(() => {
    // Agent Tutor is held behind a "Coming Soon" state for every role — see
    // AGENT_TUTOR_COMING_SOON in config.js. Guard clause, not a removed
    // effect: skip the fetch entirely so no /api/me, agent, or active-
    // session lookup fires while the flag is on. All hooks above still run
    // unconditionally on every render either way.
    if (AGENT_TUTOR_COMING_SOON) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        // /api/me and the agent lookup don't depend on each other — run them
        // together rather than paying two sequential round-trips before the
        // page can render anything.
        const [meRes, loadedAgent] = await Promise.all([
          authFetch(`${config.API_BASE_URL}/api/me`),
          getAgent(agentId),
        ]);
        const me = await handleApiResponse(meRes, navigate);
        if (me === null || cancelled) return; // handleApiResponse already sent us to /signin on a real auth failure
        // A student resuming a problem is the whole point of this page; a
        // teacher/admin previewing never has one (preview never persists —
        // see TutorWorkspace's module docstring), so skip the lookup for
        // them entirely rather than firing a request that can only ever
        // 404/empty. A failed lookup here must never block the page — it
        // just means the student starts fresh instead of resuming.
        const isStudentRole = !!me && !isStaffRole(me.role);
        let session = null;
        if (isStudentRole && loadedAgent) {
          try {
            session = await getActiveSession(agentId);
          } catch (e) {
            session = null;
          }
        }
        if (!cancelled) {
          setCurrentUser(me);
          setAgent(loadedAgent);
          setInitialSession(session);
        }
      } catch (e) {
        if (cancelled) return;
        // handleApiResponse redirects and returns null on 401 itself - it
        // never throws for that case, so anything caught here (including a
        // non-2xx ApiError) is a real load failure, not an auth problem.
        // Let the student retry instead of bouncing them out as if they'd
        // been signed out.
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, navigate, retryToken]);

  const isTeacherPreview =
    !!currentUser && isStaffRole(currentUser.role);
  const backTo = isTeacherPreview ? '/admin' : '/tutor';

  if (AGENT_TUTOR_COMING_SOON) {
    return <AgentTutorComingSoon standalone />;
  }

  if (loading) {
    return <LoadingShell />;
  }

  if (error) {
    return <ErrorState onRetry={() => setRetryToken((t) => t + 1)} />;
  }

  if (!agent) {
    return <UnavailableState onBack={() => navigate(backTo)} />;
  }

  return (
    <TutorWorkspace
      agent={agent}
      mode={isTeacherPreview ? 'preview' : 'student'}
      initialSession={initialSession}
      onExit={() => navigate(backTo)}
    />
  );
};

export default StudentTutorSession;
